import { Capacitor } from '@capacitor/core';

// Error type for consistent logging
type ErrorLog = {
  message: string;
  timestamp: number;
  path: string;
  stack?: string;
  code?: string;
  reactErrorNumber?: number;
  componentInfo?: string;
  rootElementExists?: boolean;
};

// In-memory log storage to prevent loss on crash
const errorLogs: ErrorLog[] = [];
const MAX_LOGS = 50;

/**
 * Parse React error numbers from error messages
 * e.g. "Minified React error #300" -> 300
 */
export const parseReactErrorNumber = (message: string): number | undefined => {
  const match = message.match(/Minified React error #(\d+)/);
  return match ? parseInt(match[1], 10) : undefined;
};

/**
 * Log errors with extra context information useful for debugging
 */
export const logError = (error: Error | unknown, componentInfo?: string): void => {
  try {
    // Convert unknown errors to standard format
    const err = error instanceof Error ? error : new Error(String(error));
    
    // Extract React error number if present
    const reactErrorNumber = parseReactErrorNumber(err.message);
    
    // Create structured log
    const log: ErrorLog = {
      message: err.message,
      timestamp: Date.now(),
      path: window.location.pathname,
      stack: err.stack,
      componentInfo,
      reactErrorNumber
    };
    
    // For React Error #300 specifically, add more debugging info
    if (reactErrorNumber === 300) {
      log.rootElementExists = !!document.getElementById('root');
      
      // Try to get DOM state information
      try {
        const rootContent = document.getElementById('root')?.innerHTML;
        log.componentInfo = `Root children count: ${document.getElementById('root')?.childNodes.length || 0}`;
      } catch (e) {
        log.componentInfo = 'Error getting root children';
      }
      
      // Log extra details for React Error #300
      
    }
    
    // Store log in memory
    errorLogs.unshift(log);
    if (errorLogs.length > MAX_LOGS) {
      errorLogs.pop();
    }
    
    // Output to console
    
    
    // For specific error numbers, provide hints
    if (reactErrorNumber === 300) {
      
    }
  } catch (loggingError) {
    // Fallback if logging itself fails
    
    
  }
};

/**
 * Set up global error handlers to catch unhandled errors
 */
export const setupGlobalErrorLogging = (): void => {
  // Handle regular errors
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), 'Global error handler');
    
    // Don't prevent default error handling
    return false;
  });
  
  // Handle promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason || new Error('Unhandled Promise rejection'), 'Unhandled Promise');
    
    // Don't prevent default error handling
    return false;
  });
  
  // Override console.error to capture more information
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Call original console.error
    originalConsoleError.apply(console, args);
    
    // Look for React error patterns in first argument
    const firstArg = args[0];
    if (typeof firstArg === 'string' && firstArg.includes('Minified React error #')) {
      logError(new Error(firstArg), 'Console error');
    }
  };
  
  
};

/**
 * Get all stored error logs
 */
export const getErrorLogs = (): ErrorLog[] => {
  return [...errorLogs];
};

/**
 * Clear all stored error logs
 */
export const clearErrorLogs = (): void => {
  errorLogs.length = 0;
  
};

// Expose debugging helpers to window in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).debugTools = {
    getErrorLogs,
    clearErrorLogs
  };
}

export default {
  logError,
  setupGlobalErrorLogging,
  getErrorLogs,
  clearErrorLogs
}; 